import * as i0 from '@angular/core';
import { LOCALE_ID, Pipe, Inject, Component, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@tbtk/menu';
import { MenuModule } from '@tbtk/menu';

class CasePipe {
    constructor(localeId) {
        this.localeId = localeId;
    }
    transform(value, type = 'upper', language = 'tr-TR') {
        if (!value) {
            return '';
        }
        language = language ?? this.localeId;
        switch (type) {
            case 'lower':
                return value.toLocaleLowerCase(language);
            case 'title':
                return value.toLocaleLowerCase(language).replace(/(^|\s)\S/g, (c) => c.toLocaleUpperCase(language));
            case 'sentence':
                return value.toLocaleLowerCase(language).replace(/(^|[.?!]\s+)\S/g, (c) => c.toLocaleUpperCase(language));
            case 'camel':
                return value.toLocaleLowerCase(language).replace(/[\s_-]+(\S)/g, (match, p1) => p1.toLocaleUpperCase(language));
            default:
                return value.toLocaleUpperCase(language);
        }
    }
}
CasePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: CasePipe, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
CasePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: CasePipe, name: "case" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: CasePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'case' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

/**
 * It's a component that displays a profile picture, and if no profile picture is provided, it will
 * display the first letter of the user's first name
 *
 * It also accept content children item as and display them as `tbtk-menu-item` menu item to display the content of the menu
 *
 * @description
 * The `ProfileComponent` has the following input properties:
 * - `@Input() secure` (type: `boolean | null`): A boolean value that determines whether the profile picture badge is displayed as a red or a green circle. If `null`, circle badge will not displayed.
 * - `@Input() avatar` (type: `string | null`): A URL or data URI for an image to be displayed as an user avatar in the menu item. If the `avatar` input property is not set, user first and last name first letters will be displayed as an avatar.
 * - `@Input() email` (type: `string | null`): A string that specifies user email address which will be displayed
 * - `@Input() firstName` (type: `string | null`): A property that is used to set the first name of the user.
 * - `@Input() lastName` (type: `string | null`): A property that is used to set the first name of the user.
 *
 * @example
 * <sub>app.component.html</sub>
 * Add the below code to your code stack and give initializer parameters.
 *
 * ```html
 *  <tbtk-profile [firstName]="'serkan'" [lastName]="'konakci'" email="serkan.konakci@tubitak.gov.tr">
 *   <tbtk-menu-item icon="/assets/images/icons/user.svg" class="save-icon" (click)="action('Profil Ayarları Clicked')">Profil Ayarları</tbtk-menu-item>
 *   <tbtk-menu-item icon="/assets/images/icons/question-circle.svg" class="save-icon" (click)="action('Yardım')">Yardım</tbtk-menu-item>
 *   <hr />
 *   <tbtk-menu-item icon="/assets/images/icons/logout.svg" class="save-icon" (click)="action('Çıkış')">Çıkış Yap</tbtk-menu-item>
 * </tbtk-profile>
 * ```
 */
class ProfileComponent {
    constructor() {
        this.secure = null;
        this.avatar = null;
        this.email = null;
        this._lastName = null;
        this._firstName = null;
    }
    get firstName() {
        return this._firstName;
    }
    set firstName(value) {
        this.backgroundColor = this.generateRandomDarkColor(value || '');
        this._firstName = value;
    }
    get lastName() {
        return this._lastName;
    }
    set lastName(value) {
        this.backgroundColor = this.generateRandomDarkColor(value || '');
        this._lastName = value;
    }
    generateRandomDarkColor(username) {
        let hash = 0;
        for (let i = 0; i < username.length; i++) {
            hash = username.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash % 360);
        const saturation = 58;
        const lightness = 58;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }
}
ProfileComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: ProfileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProfileComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: ProfileComponent, selector: "tbtk-profile", inputs: { secure: "secure", avatar: "avatar", email: "email", firstName: "firstName", lastName: "lastName" }, ngImport: i0, template: "<div class=\"profile-picture-container\" forMenu=\"profile-menu\">\n  <div *ngIf=\"secure\" [ngClass]=\"secure ? 'green-dot' : 'red-dot'\"></div>\n  <img [src]=\"avatar\" *ngIf=\"avatar; else profileRef\" alt=\"`${firstName} ${lastName}`\" />\n</div>\n\n<tbtk-menu id=\"profile-menu\" [arrow]=\"true\">\n  <div class=\"profile-info d-flex\">\n    <img class=\"avatar\" [src]=\"avatar\" *ngIf=\"avatar; else profileRef\" alt=\"{{ firstName }}\" />\n    <div class=\"profile-user-info\">\n      <div class=\"profile-user-name\">{{ firstName | case : 'title' }} {{ lastName | case : 'upper' : 'tr' }}</div>\n      <div class=\"profile-user-email\">{{ email }}</div>\n    </div>\n  </div>\n  <hr class=\"my-1\" />\n  <div class=\"profile-menu\">\n    <ng-content></ng-content>\n  </div>\n</tbtk-menu>\n\n<ng-template #profileRef>\n  <div class=\"profile-picture-container\" [style.background]=\"backgroundColor\">\n    {{ firstName && firstName[0] | case : 'upper' : 'tr-TR' }}{{ lastName && lastName[0] | case : 'upper' }}\n  </div>\n</ng-template>\n", styles: ["::ng-deep .tbtk-menu .profile-info .profile-picture-container,:host .profile-picture-container{position:relative;display:flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;border-radius:50%;color:var(--neutral-00);font-size:1rem;cursor:pointer}:host{display:flex;align-items:center;justify-content:center}:host .profile-picture-container .red-dot,:host .profile-picture-container .green-dot{position:absolute;top:.125rem;left:0;z-index:111;width:.5rem;height:.5rem;border-radius:50%}:host .profile-picture-container .red-dot{background:var(--danger-06)}:host .profile-picture-container .green-dot{background:var(--success-06)}::ng-deep .tbtk-menu{--popover-menu-width: fit-content}::ng-deep .tbtk-menu .profile-info{padding:1rem 1.25rem}::ng-deep .tbtk-menu .profile-info img{width:3rem;height:3rem}::ng-deep .tbtk-menu .profile-info .avatar{width:3rem;height:3rem}::ng-deep .tbtk-menu .profile-info .profile-user-info{display:flex;flex-direction:column;justify-content:center;padding-left:.875rem}::ng-deep .tbtk-menu .profile-info .profile-user-info .profile-user-name{color:var(--neutral-07);font-size:1rem;font-weight:700;line-height:1.25rem}::ng-deep .tbtk-menu .profile-info .profile-user-info .profile-user-email{color:var(--neutral-07);font-size:.75rem;font-weight:500;letter-spacing:-.017em;line-height:1rem}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.MenuComponent, selector: "tbtk-menu", inputs: ["id", "arrow", "data", "katmer"], outputs: ["active$"] }, { kind: "directive", type: i2.MenuDirective, selector: "[forContextMenu],[forMenu]", inputs: ["forContextMenu", "forMenu"] }, { kind: "pipe", type: CasePipe, name: "case" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: ProfileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbtk-profile', template: "<div class=\"profile-picture-container\" forMenu=\"profile-menu\">\n  <div *ngIf=\"secure\" [ngClass]=\"secure ? 'green-dot' : 'red-dot'\"></div>\n  <img [src]=\"avatar\" *ngIf=\"avatar; else profileRef\" alt=\"`${firstName} ${lastName}`\" />\n</div>\n\n<tbtk-menu id=\"profile-menu\" [arrow]=\"true\">\n  <div class=\"profile-info d-flex\">\n    <img class=\"avatar\" [src]=\"avatar\" *ngIf=\"avatar; else profileRef\" alt=\"{{ firstName }}\" />\n    <div class=\"profile-user-info\">\n      <div class=\"profile-user-name\">{{ firstName | case : 'title' }} {{ lastName | case : 'upper' : 'tr' }}</div>\n      <div class=\"profile-user-email\">{{ email }}</div>\n    </div>\n  </div>\n  <hr class=\"my-1\" />\n  <div class=\"profile-menu\">\n    <ng-content></ng-content>\n  </div>\n</tbtk-menu>\n\n<ng-template #profileRef>\n  <div class=\"profile-picture-container\" [style.background]=\"backgroundColor\">\n    {{ firstName && firstName[0] | case : 'upper' : 'tr-TR' }}{{ lastName && lastName[0] | case : 'upper' }}\n  </div>\n</ng-template>\n", styles: ["::ng-deep .tbtk-menu .profile-info .profile-picture-container,:host .profile-picture-container{position:relative;display:flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;border-radius:50%;color:var(--neutral-00);font-size:1rem;cursor:pointer}:host{display:flex;align-items:center;justify-content:center}:host .profile-picture-container .red-dot,:host .profile-picture-container .green-dot{position:absolute;top:.125rem;left:0;z-index:111;width:.5rem;height:.5rem;border-radius:50%}:host .profile-picture-container .red-dot{background:var(--danger-06)}:host .profile-picture-container .green-dot{background:var(--success-06)}::ng-deep .tbtk-menu{--popover-menu-width: fit-content}::ng-deep .tbtk-menu .profile-info{padding:1rem 1.25rem}::ng-deep .tbtk-menu .profile-info img{width:3rem;height:3rem}::ng-deep .tbtk-menu .profile-info .avatar{width:3rem;height:3rem}::ng-deep .tbtk-menu .profile-info .profile-user-info{display:flex;flex-direction:column;justify-content:center;padding-left:.875rem}::ng-deep .tbtk-menu .profile-info .profile-user-info .profile-user-name{color:var(--neutral-07);font-size:1rem;font-weight:700;line-height:1.25rem}::ng-deep .tbtk-menu .profile-info .profile-user-info .profile-user-email{color:var(--neutral-07);font-size:.75rem;font-weight:500;letter-spacing:-.017em;line-height:1rem}\n"] }]
        }], propDecorators: { secure: [{
                type: Input
            }], avatar: [{
                type: Input
            }], email: [{
                type: Input
            }], firstName: [{
                type: Input
            }], lastName: [{
                type: Input
            }] } });

/**
 * The profile module is used for display user information and manage user interactions.
 * It also accepts the `tbtk-menu-item` component to display the content of the menu.
 *
 * ### Installation
 *
 * All public npm packages of TUBITAK is at [https://www.npmjs.com/~tbtk](https://www.npmjs.com/~tbtk).
 * To install ProfileModule:
 *
 * ```bash
 * $ npm set registry https://registry.npmjs.org/
 * $ npm install @tbtk/profile -s
 * ```
 *
 * Just import it to your project of `@NgModule` import section.
 *
 * ```typescript
 * @NgModule({
 *   imports: [CommonModule, ProfileModule],
 * })
 * export class AppModule {
 * }
 * ```
 */
class TbtkProfileModule {
}
TbtkProfileModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: TbtkProfileModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TbtkProfileModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: TbtkProfileModule, declarations: [ProfileComponent, CasePipe], imports: [CommonModule, MenuModule], exports: [ProfileComponent] });
TbtkProfileModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: TbtkProfileModule, imports: [CommonModule, MenuModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: TbtkProfileModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ProfileComponent, CasePipe],
                    imports: [CommonModule, MenuModule],
                    exports: [ProfileComponent],
                }]
        }] });

/*
 * Public API Surface of profile
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CasePipe, ProfileComponent, TbtkProfileModule };
//# sourceMappingURL=tbtk-profile.mjs.map
